import { DEFAULT_LAYOUT_SETTINGS, sanitizeLayoutSettings } from "./layout-settings.js";
import workerSource from "layout-worker-source";

export class StableLayout {
  constructor(graph, renderer, onReady, settings = DEFAULT_LAYOUT_SETTINGS, onProgress = () => {}) {
    this.onProgress = onProgress;
    // A canonical layout viewport keeps the map identical across devices.
    // Responsive fitting belongs to the camera, not node coordinates.
    this.layoutViewport = { width: 1200, height: 800 };
    this.settings = sanitizeLayoutSettings(settings); this.appliedSettings = null;
    this.graph = graph; this.renderer = renderer; this.onReady = onReady;
    this.dragged = null; this.dragRevision = 0; this.appliedDraggedNode = null;
    this.version = 0; this.ready = false; this.paused = false;
    this.targets = new Map(); this.velocities = new Map(); this.iterations = 0;
    this.transform = { x: 1, y: 1, cx: 0, cy: 0 };
    const url = URL.createObjectURL(new Blob([workerSource], { type: "text/javascript" }));
    this.worker = new Worker(url); URL.revokeObjectURL(url);
    this.worker.onmessage = ({ data }) => this.receive(data);
    this.worker.onerror = () => { document.documentElement.dataset.forceAtlasWorker = "error"; };
    this.frame = null; this.syncTimer = null;
    renderer.setCustomBBox({ x: [-1, 1], y: [-1, 1] });
    this.animate = this.animate.bind(this);
  }
  prime(cache) {
    if (this.ready) return;
    this.warmStart = cache;
    this.warmStarted = true;
    this.receive({ version: this.version, positions: this.graph.mapNodes((id,a) => ({id,x:a.x,y:a.y})),
      transform: cache.transform, iterations: 720, settings: this.settings });
  }
  beginDrag(id) {
    if (!this.ready || !this.graph.hasNode(id)) return false;
    if (this.dragged) this.endDrag();
    this.dragged = { id, x: this.graph.getNodeAttribute(id, "x"), y: this.graph.getNodeAttribute(id, "y") };
    this.dragRevision++;
    this.graph.setNodeAttribute(id, "dragging", true);
    this.moveDragged(this.dragged);
    return true;
  }
  moveDragged(position) {
    if (!this.dragged || !this.graph.hasNode(this.dragged.id)) return;
    if (!Number.isFinite(position.x) || !Number.isFinite(position.y)) return;
    Object.assign(this.dragged, { x: position.x, y: position.y });
    this.velocities.delete(this.dragged.id);
    this.graph.mergeNodeAttributes(this.dragged.id, { x: position.x, y: position.y });
    this.sendDrag("drag");
  }
  sendDrag(type) {
    const p = this.dragged;
    this.worker.postMessage({ type, id: p.id, position: { x: p.x, y: p.y },
      dragRevision: this.dragRevision, paused: this.paused });
  }
  endDrag() {
    if (!this.dragged) return;
    const { id } = this.dragged;
    this.dragRevision++;
    this.sendDrag("release");
    this.dragged = null;
    // Do not spring back to an in-flight, pre-release simulation target.
    this.targets.delete(id); this.velocities.delete(id);
    if (this.graph.hasNode(id)) this.graph.setNodeAttribute(id, "dragging", false);
  }
  setSettings(settings) { this.settings = sanitizeLayoutSettings(settings); this.sync(); }
  sync() {
    // Invalidate in-flight results immediately, even during a burst of SSE events.
    this.version++;
    if (this.syncTimer !== null) return;
    this.syncTimer = setTimeout(() => {
      this.syncTimer = null;
      const t = this.transform;
      this.worker.postMessage({ type: "sync", version: this.version, settings: this.settings, warmStart: this.warmStart,
        nodes: this.graph.mapNodes((id, a) => ({ id, x: a.x / t.x + t.cx, y: a.y / t.y + t.cy, size: a.size })),
        viewport: this.layoutViewport,
        edges: this.graph.mapEdges((id, a, source, target) => ({ source, target })),
      });
      this.warmStart = null;
      // Initial layout is computed offscreen even for reduced-motion visitors.
      if (!this.ready || !this.paused) this.worker.postMessage({ type: "resume" });
    }, 80);
  }
  receive({ version, positions, transform, iterations, settings, dragRevision = 0, draggedNode = null, progress }) {
    if (version !== this.version || dragRevision !== this.dragRevision) return;
    if (progress !== undefined) { this.onProgress(progress); return; }
    this.appliedDraggedNode = draggedNode;
    this.iterations = iterations; this.appliedSettings = settings;
    if (!positions.every(p => Number.isFinite(p.x) && Number.isFinite(p.y))) return;
    if (!this.ready) {
      const { width, height } = this.renderer.getDimensions();
      this.transform = transform;
      this.renderer.setCustomBBox({ x: [-width / 2, width / 2], y: [-height / 2, height / 2] });
    }
    for (const id of this.velocities.keys()) if (!this.graph.hasNode(id)) this.velocities.delete(id);
    this.targets = new Map(positions.map(p => [p.id, { x: p.x, y: p.y }]));
    if (!this.ready) {
      this.graph.updateEachNodeAttributes((id, a) => ({ ...a, ...this.targets.get(id) }), { attributes: ["x", "y"] });
      this.ready = true; this.renderer.refresh(); this.fit(false); this.onReady();
      if (this.paused) this.worker.postMessage({ type: "pause" });
    }
    if (!this.paused) this.schedule();
  }
  schedule() {
    if (this.frame === null) { this.lastTime = performance.now(); this.frame = requestAnimationFrame(this.animate); }
  }
  animate(now) {
    this.frame = null;
    if (this.paused) return;
    const dt = Math.min(.034, Math.max(.001, (now - this.lastTime) / 1000)); this.lastTime = now;
    const scale = Math.max(.0001, this.renderer.getGraphToViewportRatio());
    if (!Number.isFinite(scale)) { this.frame = requestAnimationFrame(this.animate); return; }
    let moving = false;
    this.graph.updateEachNodeAttributes((id, a) => {
      if (id === this.dragged?.id) return a;
      const target = this.targets.get(id);
      if (!target) return a;
      const dx = (target.x - a.x) * scale, dy = (target.y - a.y) * scale;
      if (Math.hypot(dx, dy) < .12) { this.velocities.delete(id); return a; }
      moving = true;
      const v = this.velocities.get(id) || { x: 0, y: 0 };
      const distance = Math.hypot(dx, dy), speed = Math.min(55, distance * 3);
      const desired = { x: dx / distance * speed, y: dy / distance * speed };
      const ax = desired.x - v.x, ay = desired.y - v.y;
      const damping = Math.min(1 - Math.exp(-dt * 12), 180 * dt / (Math.hypot(ax, ay) || 1));
      v.x += ax * damping; v.y += ay * damping;
      this.velocities.set(id, v);
      return { ...a, x: a.x + v.x * dt / scale, y: a.y + v.y * dt / scale };
    }, { attributes: ["x", "y"] });
    if (moving) this.frame = requestAnimationFrame(this.animate);
  }
  setPaused(paused) {
    this.paused = paused;
    this.worker.postMessage({ type: paused ? "pause" : "resume" });
    if (paused) {
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null; this.velocities.clear();
    } else this.schedule();
  }
  fit(animate = true) {
    if (!this.graph.order || !this.ready) return;
    const { width, height } = this.renderer.getDimensions();
    const reset = { x: .5, y: .5, ratio: 1, angle: 0 };
    const points = this.graph.mapNodes((_id, a) => this.renderer.graphToViewport(a, { cameraState: reset }));
    const xs = points.map(p => p.x), ys = points.map(p => p.y);
    const xmin = Math.min(...xs), xmax = Math.max(...xs), ymin = Math.min(...ys), ymax = Math.max(...ys);
    const center = this.renderer.viewportToFramedGraph({ x: (xmin + xmax) / 2, y: (ymin + ymax) / 2 }, { cameraState: reset });
    const ratio = this.graph.order === 1 ? 1 : Math.max(.05, (xmax - xmin) / Math.max(1, width - 40), (ymax - ymin) / Math.max(1, height - 40));
    const camera = this.renderer.getCamera(), next = { ...center, ratio, angle: 0 };
    if (!animate || matchMedia("(prefers-reduced-motion: reduce)").matches) camera.setState(next);
    else camera.animate(next, { duration: 450 });
  }
  kill() {
    clearTimeout(this.syncTimer); if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.worker.terminate();
  }
}
